/**
 * Authoritative mapping of Herrenknecht sensor reference IDs to their display
 * name, unit and dashboard category. Ported from the old API_TBM.py tbm_tags.
 */

export type ParameterId =
  | "chainage"
  | "advanceRate"
  | "rpm"
  | "jackingForce"
  | "torque"
  | "facePressure"
  | "feedLinePressure"
  | "slurryLinePressure"
  | "feedFlowRate"
  | "returnFlowRate"
  | "totalScForce"
  | "sc1"
  | "sc2"
  | "sc3"
  | "sc4"
  | "bypassStatus"
  | "activeJackStation"
  | "oilTempContainer"
  | "oilTempMachine"
  | "coolingWaterTemp"
  | "oilTempStatus";

export type ParameterGroup = "drive" | "pressure" | "flow" | "steering" | "status" | "jacks" | "machine";

export interface ParameterDef {
  id: ParameterId;
  referenceId: string;
  displayName: string;
  shortName: string;
  unit: string;
  group: ParameterGroup;
  precision: number;
}

export const PARAMETERS: Record<ParameterId, ParameterDef> = {
  chainage:           { id: "chainage",           referenceId: "V34_TL_SR_m_Z",             displayName: "Chainage",              shortName: "Chainage",     unit: "m",     group: "drive",     precision: 2 },
  advanceRate:        { id: "advanceRate",        referenceId: "V34_VTgeschw_Z",            displayName: "Advance Rate",          shortName: "Advance Rate", unit: "mm/min", group: "drive",    precision: 1 },
  rpm:                { id: "rpm",                referenceId: "V13_SR_Drehz_nach_Abgl_Z",  displayName: "Cutterhead RPM",        shortName: "RPM",          unit: "rpm",   group: "drive",     precision: 2 },
  jackingForce:       { id: "jackingForce",       referenceId: "V15_VTP_D_Kraft_Z",         displayName: "Jacking Force",         shortName: "Jacking Force", unit: "kN",   group: "drive",     precision: 0 },
  torque:             { id: "torque",             referenceId: "V13_SR_ArbDr_Z",            displayName: "Cutterhead Torque",     shortName: "Torque",       unit: "bar",   group: "drive",     precision: 1 },
  facePressure:       { id: "facePressure",       referenceId: "V13_Erddruck_01_Z",         displayName: "Face Pressure",         shortName: "Face P",       unit: "bar",   group: "pressure",  precision: 2 },
  feedLinePressure:   { id: "feedLinePressure",   referenceId: "V21_SL_Dr_Z",               displayName: "Feed Line Pressure",    shortName: "Feed P",       unit: "bar",   group: "pressure",  precision: 2 },
  slurryLinePressure: { id: "slurryLinePressure", referenceId: "V21_FL_Dr_Z",               displayName: "Slurry Line Pressure",  shortName: "Slurry P",     unit: "bar",   group: "pressure",  precision: 2 },
  feedFlowRate:       { id: "feedFlowRate",       referenceId: "V21_SL_Dfl_Z",              displayName: "Feed Flow Rate",        shortName: "Feed Q",       unit: "m³/h",  group: "flow",      precision: 1 },
  returnFlowRate:     { id: "returnFlowRate",     referenceId: "V21_FL_Dfl_Z",              displayName: "Return Flow Rate",      shortName: "Return Q",     unit: "m³/h",  group: "flow",      precision: 1 },
  totalScForce:       { id: "totalScForce",       referenceId: "V18_GesamtKraft_STZ_Z",     displayName: "Total SC Force",        shortName: "Total SC",     unit: "kN",    group: "steering",  precision: 0 },
  sc1:                { id: "sc1",                referenceId: "V18_STZ1_Dr_Z",             displayName: "SC 1 Pressure",         shortName: "SC 1",         unit: "bar",   group: "steering",  precision: 1 },
  sc2:                { id: "sc2",                referenceId: "V18_STZ2_Dr_Z",             displayName: "SC 2 Pressure",         shortName: "SC 2",         unit: "bar",   group: "steering",  precision: 1 },
  sc3:                { id: "sc3",                referenceId: "V18_STZ3_Dr_Z",             displayName: "SC 3 Pressure",         shortName: "SC 3",         unit: "bar",   group: "steering",  precision: 1 },
  sc4:                { id: "sc4",                referenceId: "V18_STZ4_Dr_Z",             displayName: "SC 4 Pressure",         shortName: "SC 4",         unit: "bar",   group: "steering",  precision: 1 },
  bypassStatus:       { id: "bypassStatus",       referenceId: "V21_V30_BS_Status_Z",       displayName: "Bypass Status",         shortName: "Bypass",       unit: "",      group: "status",    precision: 0 },
  activeJackStation:  { id: "activeJackStation",  referenceId: "V15_DNR_Z",                 displayName: "Active Jack Station",   shortName: "Active Jack",  unit: "",      group: "jacks",     precision: 0 },
  oilTempContainer:   { id: "oilTempContainer",   referenceId: "V11_Oeltemp_C_Z",           displayName: "Oil Temp (Container)",  shortName: "Oil Temp C",   unit: "°C",    group: "machine",   precision: 1 },
  oilTempMachine:     { id: "oilTempMachine",     referenceId: "V11_Oeltemp_M_Z",           displayName: "Oil Temp (Machine)",    shortName: "Oil Temp M",   unit: "°C",    group: "machine",   precision: 1 },
  coolingWaterTemp:   { id: "coolingWaterTemp",   referenceId: "V11_Kuehlwassser_Temp_Z",   displayName: "Cooling Water Temp",    shortName: "Water Temp",   unit: "°C",    group: "machine",   precision: 1 },
  oilTempStatus:      { id: "oilTempStatus",      referenceId: "V11_Oeltemp_Status_Z",      displayName: "Oil Temp Status",       shortName: "Oil Status",   unit: "",      group: "machine",   precision: 0 },
};

export const PARAMETER_LIST: ParameterDef[] = Object.values(PARAMETERS);

export const REFERENCE_ID_TO_PARAMETER: Record<string, ParameterDef> = Object.fromEntries(
  PARAMETER_LIST.map((p) => [p.referenceId, p]),
);

export const PARAMETER_ID_TO_REFERENCE_ID: Record<ParameterId, string> = Object.fromEntries(
  PARAMETER_LIST.map((p) => [p.id, p.referenceId]),
) as Record<ParameterId, string>;

/** Reference IDs the dashboard's primary parameter set requires. */
export const PRIMARY_REFERENCE_IDS: string[] = PARAMETER_LIST.map((p) => p.referenceId);

export type BypassTone = "ok" | "warn" | "error";

export function bypassStatusLabel(value: number | null | undefined): {
  label: string;
  tone: BypassTone;
} {
  // From the old API_TBM.py: bypass_status == 2 → mining mode (closed),
  // bypass_status == 1 → bypass valve open. Anything else (null, NaN,
  // unknown code) is treated as an error state.
  if (value === 2) return { label: "BYPASS CLOSED", tone: "ok" };
  if (value === 1) return { label: "BYPASS OPEN", tone: "warn" };
  return { label: "ERROR STATE", tone: "error" };
}
